// Adapted from OpenCharts (MIT, see LICENSE-OpenCharts).
import { dist, distToSegment, type Pt, pointInBox } from "./geometry";
import type { Hit, PriceKey, ResolvedEntry, TimeKey } from "./types";

const HANDLE_TOLERANCE = 8;
const LINE_TOLERANCE = 6;
/** Text drawings' font size (px). */
export const TEXT_SIZE = 14;

interface Tol {
  handle: number;
  line: number;
}

/**
 * The drawing under p: the topmost (last drawn) wins. `scale` grows the
 * tolerances, ~2 for touch.
 */
export function hitTest(entries: ResolvedEntry[], p: Pt, scale = 1): Hit | null {
  const tol: Tol = { handle: HANDLE_TOLERANCE * scale, line: LINE_TOLERANCE * scale };
  for (let i = entries.length - 1; i >= 0; i--) {
    const hit = hitEntry(entries[i]!, p, tol);
    if (hit) return hit;
  }
  return null;
}

function hitEntry(e: ResolvedEntry, p: Pt, tol: Tol): Hit | null {
  switch (e.d.type) {
    case "trendline":
    case "arrow":
      return hitTrendline(e, p, tol);
    case "horizontal":
      return hitHorizontal(e, p, tol);
    case "vertical":
      return hitVertical(e, p, tol);
    case "rectangle":
    case "ellipse":
    case "triangle":
      return hitBoxShape(e, p, tol);
    case "fibonacci":
    case "fibextension":
      return hitFibonacci(e, p, tol);
    case "position":
      return hitPosition(e, p, tol);
    case "channel":
      return hitChannel(e, p, tol);
    case "text":
      return hitText(e, p, tol);
    default:
      return null;
  }
}

function pointHit(e: ResolvedEntry, timeKey: TimeKey | null, priceKey: PriceKey | null): Hit {
  return { id: e.d.id, region: { kind: "point", timeKey, priceKey } };
}

function bodyHit(e: ResolvedEntry): Hit {
  return { id: e.d.id, region: { kind: "body" } };
}

/** The two anchor handles every two-point drawing has. */
function anchorHit(e: ResolvedEntry, p: Pt, tol: Tol): Hit | null {
  if (e.x1 !== null && e.y1 !== null && dist(p, { x: e.x1, y: e.y1 }) <= tol.handle) {
    return pointHit(e, "time", "price");
  }
  if (e.x2 !== null && e.y2 !== null && dist(p, { x: e.x2, y: e.y2 }) <= tol.handle) {
    return pointHit(e, "time2", "price2");
  }
  return null;
}

function hitTrendline(e: ResolvedEntry, p: Pt, tol: Tol): Hit | null {
  const anchor = anchorHit(e, p, tol);
  if (anchor) return anchor;
  const seg =
    e.seg ??
    (e.x1 !== null && e.y1 !== null && e.x2 !== null && e.y2 !== null
      ? { x1: e.x1, y1: e.y1, x2: e.x2, y2: e.y2 }
      : null);
  if (!seg) return null;
  return distToSegment(p, { x: seg.x1, y: seg.y1 }, { x: seg.x2, y: seg.y2 }) <= tol.line ? bodyHit(e) : null;
}

// A horizontal line only has a price: dragging it anywhere moves the price.
function hitHorizontal(e: ResolvedEntry, p: Pt, tol: Tol): Hit | null {
  if (e.y1 === null || Math.abs(p.y - e.y1) > tol.line) return null;
  return pointHit(e, null, "price");
}

function hitVertical(e: ResolvedEntry, p: Pt, tol: Tol): Hit | null {
  if (e.x1 === null || Math.abs(p.x - e.x1) > tol.line) return null;
  return pointHit(e, "time", null);
}

// Rectangles and shapes: a corner resizes, inside the box moves it.
function hitBoxShape(e: ResolvedEntry, p: Pt, tol: Tol): Hit | null {
  if (e.x1 === null || e.y1 === null || e.x2 === null || e.y2 === null) return null;
  const corner = rectCornerHit(e, p, tol);
  if (corner) return corner;
  return pointInBox(p, e.x1, e.y1, e.x2, e.y2, tol.line) ? bodyHit(e) : null;
}

// Each corner maps to the time/price keys it controls, so a derived corner
// (x1, y2) resizes through time + price2.
function rectCornerHit(e: ResolvedEntry, p: Pt, tol: Tol): Hit | null {
  const corners = [
    { x: e.x1!, y: e.y1!, timeKey: "time", priceKey: "price" },
    { x: e.x2!, y: e.y2!, timeKey: "time2", priceKey: "price2" },
    { x: e.x1!, y: e.y2!, timeKey: "time", priceKey: "price2" },
    { x: e.x2!, y: e.y1!, timeKey: "time2", priceKey: "price" },
  ] as const;
  for (const c of corners) {
    if (dist(p, c) <= tol.handle) return pointHit(e, c.timeKey, c.priceKey);
  }
  return null;
}

function hitFibonacci(e: ResolvedEntry, p: Pt, tol: Tol): Hit | null {
  const anchor = anchorHit(e, p, tol);
  if (anchor) return anchor;
  if (e.x1 === null || e.x2 === null || !e.fibLevels) return null;
  if (p.x < Math.min(e.x1, e.x2) - tol.line || p.x > Math.max(e.x1, e.x2) + tol.line) return null;
  return e.fibLevels.some(l => l.y !== null && Math.abs(p.y - l.y) <= tol.line) ? bodyHit(e) : null;
}

// Entry / target / stop handles sit at the box's middle; the right edge
// resizes its width. Body = anywhere in the box.
function hitPosition(e: ResolvedEntry, p: Pt, tol: Tol): Hit | null {
  const { x1, x2, y1, yStop, yTarget } = e;
  if (x1 === null || x2 === null || y1 === null) return null;
  const xa = Math.min(x1, x2);
  const xb = Math.max(x1, x2);
  const xm = (xa + xb) / 2;
  if (yTarget != null && dist(p, { x: xm, y: yTarget }) <= tol.handle) return pointHit(e, null, "targetPrice");
  if (yStop != null && dist(p, { x: xm, y: yStop }) <= tol.handle) return pointHit(e, null, "stopPrice");
  if (dist(p, { x: xm, y: y1 }) <= tol.handle) return pointHit(e, null, "price");
  if (dist(p, { x: xb, y: y1 }) <= tol.handle) return pointHit(e, "time2", null);
  const ys = [y1, yStop ?? y1, yTarget ?? y1];
  const inside =
    p.x >= xa - tol.line &&
    p.x <= xb + tol.line &&
    p.y >= Math.min(...ys) - tol.line &&
    p.y <= Math.max(...ys) + tol.line;
  return inside ? bodyHit(e) : null;
}

function hitChannel(e: ResolvedEntry, p: Pt, tol: Tol): Hit | null {
  const anchor = anchorHit(e, p, tol);
  if (anchor) return anchor;
  if (e.x1 !== null && e.y3 != null && dist(p, { x: e.x1, y: e.y3 }) <= tol.handle) {
    return pointHit(e, "time3", "price3");
  }
  if (e.x1 === null || e.y1 === null || e.x2 === null || e.y2 === null || e.y3 == null) return null;
  const dy = e.y3 - e.y1;
  const onMain = distToSegment(p, { x: e.x1, y: e.y1 }, { x: e.x2, y: e.y2 }) <= tol.line;
  const onOff = distToSegment(p, { x: e.x1, y: e.y3 }, { x: e.x2, y: e.y2 + dy }) <= tol.line;
  return onMain || onOff ? bodyHit(e) : null;
}

/** Text box size (px) for a font size: the renderer and the hit test agree. */
export function textBox(text: string, fontSize: number): { w: number; h: number } {
  const lines = text.split("\n");
  const longest = lines.reduce((m, l) => Math.max(m, l.length), 4);
  return { w: longest * fontSize * 0.6, h: lines.length * fontSize * 1.3 };
}

function hitText(e: ResolvedEntry, p: Pt, tol: Tol): Hit | null {
  if (e.x1 === null || e.y1 === null) return null;
  const { w, h } = textBox(e.d.text || "Text", TEXT_SIZE);
  const inside =
    p.x >= e.x1 - tol.line && p.x <= e.x1 + w + tol.line && p.y >= e.y1 - tol.line && p.y <= e.y1 + h + tol.line;
  return inside ? pointHit(e, "time", "price") : null;
}
